import { css, html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { LitElementBase } from "../LitElementBase";

type Point = [number, number];
type Edge = [number, number];

@customElement("ehm-pattern")
export class EhmNetwork extends LitElementBase {
  // noinspection CssUnresolvedCustomProperty
  static styles = css`
    :host {
      position: relative;
      display: block;
    }
    canvas {
      position: absolute;
      right: 0;
      top: 0;
      width: var(--pattern-width, 511px);
      height: var(--pattern-height, 500px);
      display: block;
      pointer-events: none;
    }
  `;

  // Visual parameters (can be tuned via attributes)
  @property({ type: Number, attribute: "move-radius" })
  moveRadius: number = 25;

  @property({ type: Number })
  speed: number = 0.04;

  @property({ type: Number })
  jitter: number = 0;

  @property({ type: Number, attribute: "dot-radius" })
  dotRadius: number = 10;

  @property({ type: Number, attribute: "line-width" })
  lineWidth: number = 1.25;

  // If edges is empty, nearest-neighbor graph is used with this many neighbors per node
  @property({ type: Number })
  neighbors: number = 0;

  // Define points and edges as properties for potential future configurability.
  // For now, shipped with sensible defaults from the NJK example.
  @state()
  private points: Point[] = [
    [0.5, 0.25],
    [0.93, 0.16],
    [0.8, 0.76],
    [0.44, 0.97],
    [0.16, 0.57],
    [0.52, 0.52],
    [0.47, -0.1],
    [-0.7, -0.8],
    [1.04, 0.95],
    [1.05, 0.06]
  ];

  @state()
  private edges: Edge[] = [
    [5, 0],
    [5, 1],
    [5, 2],
    [5, 3],
    [5, 4],
    [1, 2],
    [2, 3],
    [3, 4],
    [4, 0],
    [6, 0],
    [7, 0],
    [9, 0],
    [2, 8]
  ];

  // Rendering/animation internals
  #canvas?: HTMLCanvasElement;
  #ctx?: CanvasRenderingContext2D | null;
  #dpr = 1;
  #resizeObserver?: ResizeObserver;
  #rafId: number | null = null;
  #reduceMotionMql?: MediaQueryList;
  #prefersReducedMotion = false;
  #onReduceMotionChange = (e: MediaQueryListEvent) => {
    this.#prefersReducedMotion = e.matches;
    if (this.#prefersReducedMotion) {
      // Stop animating and render a single static frame
      if (this.#rafId !== null) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = null;
      }
      this.#step();
    } else {
      // Resume animation
      if (this.#rafId === null) {
        this.#loop();
      }
    }
  };

  // Node model
  #nodes: Array<{ x0: number; y0: number; x: number; y: number; vx: number; vy: number }> = [];

  private getColors() {
    const styles = getComputedStyle(this);
    const COLOR_LINE = (styles.getPropertyValue("--pattern-line").trim() || "#2d3573").toString();
    const COLOR_DOT = (styles.getPropertyValue("--pattern-dot").trim() || "#2d3573").toString();
    const COLOR_DOT_ALT = (styles.getPropertyValue("--pattern-dot-alt").trim() || "#e4e866").toString();
    return { COLOR_LINE, COLOR_DOT, COLOR_DOT_ALT };
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.#dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    // Setup prefers-reduced-motion
    this.#reduceMotionMql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#prefersReducedMotion = this.#reduceMotionMql.matches;
    this.#reduceMotionMql.addEventListener("change", this.#onReduceMotionChange);
  }

  firstUpdated(): void {
    this.#canvas = this.renderRoot.querySelector("#pattern") as HTMLCanvasElement;
    this.#ctx = this.#canvas?.getContext("2d");
    this.#fitCanvas();

    // Map points to pixels and build nodes
    this.#nodes = this.#mapPointsToPixels(this.points).map(([x, y]) => ({
      x0: x,
      y0: y,
      x,
      y,
      vx: (Math.random() - 0.5) * this.speed * 2,
      vy: (Math.random() - 0.5) * this.speed * 2
    }));

    if (!this.edges || this.edges.length === 0) {
      this.edges = this.#buildEdges(this.neighbors);
    }

    // Keep design aligned on resize
    this.#resizeObserver = new ResizeObserver(() => {
      this.#fitCanvas();
      const mapped = this.#mapPointsToPixels(this.points);
      mapped.forEach(([x, y], i) => {
        const n = this.#nodes[i];
        n.x0 = x;
        n.y0 = y;
        n.x = x;
        n.y = y;
      });
      if (!this.edges || this.edges.length === 0) {
        this.edges = this.#buildEdges(this.neighbors);
      }
    });
    this.#resizeObserver.observe(this.#canvas!);

    // Start animation unless reduced motion is preferred
    if (this.#prefersReducedMotion) {
      this.#step();
    } else {
      this.#loop();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.#resizeObserver) this.#resizeObserver.disconnect();
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    if (this.#reduceMotionMql) {
      this.#reduceMotionMql.removeEventListener("change", this.#onReduceMotionChange);
    }
  }

  // Utility: map % points -> pixel positions for current canvas size
  #mapPointsToPixels(points: Point[]): Point[] {
    const w = this.#canvas!.clientWidth;
    const h = this.#canvas!.clientHeight;
    return points.map(([px, py]) => [px * w, py * h]);
  }

  // Utility: nearest-neighbor edges if not provided
  #buildEdges(k: number): Edge[] {
    const edges = new Set<string>();
    for (let i = 0; i < this.#nodes.length; i++) {
      const d = this.#nodes
        .map((n, j) => ({
          j,
          dist: i === j ? Infinity : (this.#nodes[i].x - n.x) ** 2 + (this.#nodes[i].y - n.y) ** 2
        }))
        .sort((a, b) => a.dist - b.dist)
        .slice(0, k);
      for (const { j } of d) edges.add(`${Math.min(i, j)}-${Math.max(i, j)}`);
    }
    return Array.from(edges, (key) => key.split("-").map(Number) as Edge);
  }

  // Canvas fit to CSS size and DPR
  #fitCanvas() {
    if (!this.#canvas || !this.#ctx) return;
    const { width, height } = this.#canvas.getBoundingClientRect();
    this.#canvas.width = Math.floor(width * this.#dpr);
    this.#canvas.height = Math.floor(height * this.#dpr);
    this.#ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
  }

  // Animation loop
  #loop = () => {
    this.#step();
    this.#rafId = requestAnimationFrame(this.#loop);
  };

  // Single frame step
  #step() {
    if (!this.#ctx || !this.#canvas) return;
    const { COLOR_LINE, COLOR_DOT, COLOR_DOT_ALT } = this.getColors();

    // Move nodes within a circle around home
    for (const n of this.#nodes) {
      n.vx = this.#clamp(n.vx + this.#rnd(-this.jitter, this.jitter), -this.speed, this.speed);
      n.vy = this.#clamp(n.vy + this.#rnd(-this.jitter, this.jitter), -this.speed, this.speed);

      n.x += n.vx;
      n.y += n.vy;

      const dx = n.x - n.x0;
      const dy = n.y - n.y0;
      const dist = Math.hypot(dx, dy);
      if (dist > this.moveRadius) {
        const a = Math.atan2(dy, dx);
        n.x = n.x0 + Math.cos(a) * this.moveRadius;
        n.y = n.y0 + Math.sin(a) * this.moveRadius;
        n.vx *= -1;
        n.vy *= -1;
      }
    }

    // Draw
    this.#ctx.clearRect(0, 0, this.#canvas.width, this.#canvas.height);

    // Lines
    this.#ctx.beginPath();
    this.#ctx.lineWidth = this.lineWidth;
    this.#ctx.strokeStyle = COLOR_LINE;
    for (const [a, b] of this.edges) {
      const na = this.#nodes[a];
      const nb = this.#nodes[b];
      this.#ctx.moveTo(na.x, na.y);
      this.#ctx.lineTo(nb.x, nb.y);
    }
    this.#ctx.stroke();

    // Dots
    this.#nodes.forEach((n, i) => {
      this.#ctx!.beginPath();
      this.#ctx!.arc(n.x, n.y, this.dotRadius, 0, Math.PI * 2);
      this.#ctx!.fillStyle = i === Math.floor(this.#nodes.length / 2) ? COLOR_DOT_ALT : COLOR_DOT;
      this.#ctx!.fill();
    });
  }

  // Helpers
  #clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
  }

  #rnd(min: number, max: number) {
    return Math.random() * (max - min) + min;
  }

  render(): TemplateResult {
    return html`
      <canvas id="pattern" aria-hidden="true"></canvas>
    `;
  }
}
